import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import collections from "../../.source/browser";
import { DocsLayout } from "fumadocs-ui/layouts/notebook";
import {
  DocsBody,
  DocsDescription,
  DocsPage,
  DocsTitle,
  MarkdownCopyButton,
} from "fumadocs-ui/layouts/notebook/page";

import { getMDXComponents } from "../components/mdx";
import { PageOpenMenu } from "../components/page-open-menu";
import { buildDocHead } from "../lib/doc-head";
import { baseOptions, DocsFooter } from "../lib/layout.shared";
import type { NavTitles } from "../lib/navigation";
import {
  buildPageTree,
  navPagePaths,
  navSectionForPath,
} from "../lib/navigation";
import { getMarkdownResponse } from "../lib/raw-documents";
import { source } from "../lib/source";

type PageData = {
  path: string;
  url: string;
  title: string;
  description?: string;
  full?: boolean;
  section?: string;
  navTitles: NavTitles;
};

// Sidebar labels for the manifest, resolved once from the source (server-only)
// and shipped as plain data so the client builds the tree without importing
// the source runtime.
let navTitlesCache: NavTitles | null = null;
function computeNavTitles(): NavTitles {
  if (navTitlesCache) return navTitlesCache;
  const titles: NavTitles = {};
  for (const path of navPagePaths()) {
    const page = source.getPage(path.split("/"));
    if (!page) {
      throw new Error(`Navigation page does not exist: ${path}`);
    }
    titles[path] = page.data.sidebarTitle ?? page.data.title;
  }
  navTitlesCache = titles;
  return titles;
}

const loadPage = createServerFn({ method: "GET" })
  .validator((slugs: string[]) => slugs)
  .handler(async ({ data: slugs }) => {
    const page = source.getPage(slugs);

    if (!page) {
      // oxlint-disable-next-line typescript/only-throw-error -- TanStack Router control flow signals are not Error instances.
      throw notFound();
    }

    const data: PageData = {
      path: page.path,
      url: page.url,
      title: page.data.title,
      navTitles: computeNavTitles(),
    };

    if (page.data.description) data.description = page.data.description;
    if (page.data.full || page.data.mode === "wide") data.full = true;
    const section = navSectionForPath(slugs.join("/"));
    if (section) data.section = section;

    return data;
  });

const clientLoader = collections.docs.createClientLoader<PageData>({
  id: "docs",
  component(loaded, page) {
    const MDX = loaded.default;
    const markdownUrl = `${page.url}.md`;

    return (
      <DocsPage
        toc={loaded.toc}
        full={page.full}
        footer={{ children: <DocsFooter /> }}
      >
        {page.section ? (
          <p className="docs-page-section">{page.section}</p>
        ) : null}
        <DocsTitle>{page.title}</DocsTitle>
        <DocsDescription>{page.description}</DocsDescription>
        <div className="flex flex-row items-center gap-2 border-b pt-2 pb-6">
          <MarkdownCopyButton markdownUrl={markdownUrl} />
          <PageOpenMenu markdownUrl={markdownUrl} pageUrl={page.url} />
        </div>
        <DocsBody>
          <MDX components={getMDXComponents()} />
        </DocsBody>
      </DocsPage>
    );
  },
});

export const Route = createFileRoute("/$")({
  server: {
    handlers: {
      GET: async ({ request, next }) =>
        (await getMarkdownResponse(request)) ?? next(),
    },
  },
  loader: async ({ params }) => {
    const slugs = params._splat ? params._splat.split("/") : [];
    const page = await loadPage({ data: slugs });
    await clientLoader.preload(page.path);
    return page;
  },
  head: ({ loaderData }) =>
    loaderData ? buildDocHead(loaderData) : { links: [], meta: [] },
  component: Page,
});

function Page() {
  const page = Route.useLoaderData();
  const Content = clientLoader.getComponent(page.path);

  return (
    <DocsLayout
      tree={buildPageTree(page.navTitles)}
      tabMode="navbar"
      tabs={{
        transform: (tab) => ({
          ...tab,
          title: (
            <>
              {/* The navbar tab row renders only the title, so the icon is
                  embedded here. The sidebar's tabs dropdown renders tab.icon
                  on its own too; styles.css hides this embedded copy there
                  so the icon does not render twice. */}
              <span className="docs-tab-title-icon">{tab.icon}</span>
              {tab.title}
            </>
          ),
        }),
      }}
      sidebar={{ collapsible: false }}
      {...baseOptions()}
    >
      <Content {...page} />
    </DocsLayout>
  );
}
